import { GridColDef, GridSortModel, GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { deleteExportFile, getAllExportFiles } from 'src/shared/services/exportFileService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { Clear, Download } from '@mui/icons-material';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { downloadExportFile } from 'src/shared/functions/downloadExportFile';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import dayjs from 'dayjs';
import { ExportFileResponse } from '../models/exportFileResponse';
import { ExportFileSearchFilter } from '../models/exportFilterSearchFilter';
import { MyExportsFilePresentation } from './myExportsFilePresentation';

type MyExportsDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchFilter: ExportFileSearchFilter;
  searchFilterCallback: (filter: ExportFileSearchFilter) => void;
};

interface DisplayExportFileResponse extends ExportFileResponse {
  download: string;
}

export const MyExportsDataGrid = (props: MyExportsDataGridProps) => {
  const { loadingCallback, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['file']);
  const isMounted = useIsMounted();

  const [downloadingFile, setDownloadingFile] = useState<boolean>(false);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  const [rows, setRows] = useState<DisplayExportFileResponse[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: [10, 15, 25, 50],
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
      loadingCallback(true);
    }

    try {
      const result = await getAllExportFiles(searchFilter);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(
            result.data.results.map((item) => {
              return { ...item, download: '' };
            }),
          );
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
        loadingCallback(false);
      }
    }
  }, [isMounted, loadingCallback, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [searchFilter, fetchData, isMounted]);

  const downloadCallback = useCallback((isLoading: boolean) => {
    setDownloadingFile(isLoading);
  }, []);

  const downloadFile = async (id, fileName, fileType) => {
    downloadExportFile(id, fileName, fileType, downloadCallback);
  };

  // API:t raderar en fil per anrop, så de valda filerna raderas parallellt. allSettled gör att
  // ett misslyckat anrop inte stoppar de andra, och att vi kan räkna hur många som inte gick att radera.
  const handleDeleteConfirm = async () => {
    const ids = selectionModel.map(Number);
    setDeleting(true);
    try {
      const results = await Promise.allSettled(ids.map((id) => deleteExportFile(id)));
      const failedCount = results.filter((x) => x.status === 'rejected').length;
      const deletedCount = ids.length - failedCount;
      if (deletedCount > 0) {
        toast.success(
          <>{deletedCount === 1 ? t('file:myExports.deleteSuccess') : t('file:myExports.deleteSuccessPlural', { count: deletedCount })}</>,
        );
      }
      if (failedCount > 0) {
        toast.error(<>{t('file:myExports.deleteFailed', { count: failedCount })}</>);
      }
    } finally {
      if (isMounted()) {
        setDeleting(false);
        setShowConfirmDeleteDialog(false);
        setSelectionModel([]);
        fetchData();
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'filename',
      headerName: t('file:tableHeaders.exportType'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <MyExportsFilePresentation exportFileResponse={params.row} />;
      },
    },
    {
      field: 'createdAt',
      headerName: t('file:tableHeaders.createdAt'),
      sortable: true,
      flex: 0.8,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.createdAt) || ''}</>;
      },
    },
    {
      field: 'createdBy',
      headerName: t('file:tableHeaders.createdBy'),
      sortable: true,
      flex: 1.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.createdBy}</>;
      },
    },
    {
      field: 'mediaType',
      headerName: t('file:tableHeaders.mediaType'),
      sortable: false,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.mediaType}</>;
      },
    },

    {
      field: 'id',
      headerName: '',
      sortable: false,
      flex: 0.1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.id}</>;
      },
    },
    {
      field: 'download',
      headerName: ' ',
      sortable: false,
      minWidth: 130,
      flex: 0.7,
      renderCell: (params: GridCellParams) => {
        return (
          <Button
            endIcon={<Download fontSize='small' />}
            sx={{ whiteSpace: 'nowrap' }}
            onClick={(e) => {
              e.stopPropagation();
              const exportType = t(`common:exportFileType.${params.row.exportType}`);
              const dateTime = `${DateHelper.getFSDate(params.row.createdAt)}_${dayjs(params.row.createdAt).format('HHMM')}`;
              const fileName = `${exportType}_${dateTime}.${params.row.fileExtension}`;
              downloadFile(params.row.id, fileName, params.row.filetype);
            }}
          >
            {t('file:downloadButton')}
          </Button>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          py: 1,
        },
      }}
    >
      <LoadingOverlay message={t('file:downloadInProgress')} loading={downloadingFile} />
      <DataGrid<DisplayExportFileResponse>
        columns={columns}
        rows={rows}
        autoHeight
        columnVisibilityModel={{ id: false }}
        dataModel={data}
        checkboxSelection
        disableSelectionOnClick
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        disablePointer
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          ConsoleHelper.log('newSortModel', newSortModel);
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        selectionModel={selectionModel}
        onSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
      />
      {selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }} aria-label={t('common:button.close')}>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            <Button variant='contained' disableElevation color='error' sx={{ mr: 1 }} onClick={() => setShowConfirmDeleteDialog(true)}>
              {t('common:button.delete')}
            </Button>
          </Box>
        </FixedFooter>
      )}
      {showConfirmDeleteDialog && (
        <DeleteConfirmationDialog
          open={showConfirmDeleteDialog}
          title={t('file:myExports.deleteConfirmTitle')}
          value=''
          modaltext={
            selectionModel.length === 1
              ? t('file:myExports.deleteConfirmDescription')
              : t('file:myExports.deleteConfirmDescriptionPlural', { count: selectionModel.length })
          }
          confirmButtonText={t('common:button.delete')}
          confirmButtonColor='error'
          onClose={(value) => {
            if (value && !deleting) {
              handleDeleteConfirm();
            } else if (!value) {
              setShowConfirmDeleteDialog(false);
            }
          }}
        />
      )}
    </Box>
  );
};
